import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from "react";
import type { Identity } from "@agriflow/shared";
import { api } from "./api";

interface AuthValue {
  identity: Identity | null;
  loading: boolean;
  organizationId: string | null;
  selectOrganization(id: string): void;
  refresh(): Promise<void>;
  logout(): Promise<void>;
}
const AuthContext = createContext<AuthValue | null>(null);
export function AuthProvider({ children }: PropsWithChildren) {
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [loading, setLoading] = useState(true);
  const [organizationId, setOrganizationId] = useState(localStorage.getItem("agriflow.organization"));
  const refresh = async () => {
    try {
      const response = await api.me();
      setIdentity(response.data);
      const valid = response.data.memberships.some((item) => item.organizationId === organizationId);
      if (!valid && response.data.memberships[0]) {
        localStorage.setItem("agriflow.organization", response.data.memberships[0].organizationId);
        setOrganizationId(response.data.memberships[0].organizationId);
      }
    } catch { setIdentity(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { void refresh(); }, []);
  useEffect(() => {
    if (window.parent === window) return;
    let completed = false;
    const requestSession = () => {
      if (!completed) window.parent.postMessage({ type: "agriflow:sso:request" }, "*");
    };
    const handleMessage = async (event: MessageEvent) => {
      if (event.source !== window.parent || !event.data || typeof event.data !== "object") return;
      const message = event.data as Record<string, unknown>;
      if (message.type === "agriflow:sso:logout") {
        completed = true;
        await api.logout();
        setIdentity(null);
        setOrganizationId(null);
        return;
      }
      if (message.type !== "agriflow:sso:token" || typeof message.token !== "string") return;
      try {
        await api.loginWithMammouth(message.token);
        completed = true;
        await refresh();
      } catch {
        setIdentity(null);
      }
    };
    window.addEventListener("message", handleMessage);
    requestSession();
    const interval = window.setInterval(requestSession, 1_000);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("message", handleMessage);
    };
  }, []);
  const value = useMemo<AuthValue>(() => ({
    identity, loading, organizationId,
    selectOrganization(id) { localStorage.setItem("agriflow.organization", id); setOrganizationId(id); },
    refresh,
    async logout() { await api.logout(); localStorage.removeItem("agriflow.organization"); setIdentity(null); setOrganizationId(null); }
  }), [identity, loading, organizationId]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("AuthProvider is missing");
  return context;
}
